import { EditIcon, Group, Statistic } from '@/components/_common';
import {
  WorkOrderItemType,
  WorkOrderItemOrigin,
  WorkOrderItemStatus,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import ItemStatusTag from '../../ItemStatusTag';
import { useIntl } from 'react-intl';
import { WorkOrderItemData } from '..';
import { CSSProperties } from 'react';
import StatusTag from '@/components/_common/StatusTag';
import { Modal, Typography, Divider, Row, Col } from 'antd';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';
import ChangeStatusItemModal from '../../ChangeStatusItemModal';
import { useDisclosure } from '@/hooks';

interface Props {
  workOrderItem: WorkOrderItemData;
}

export default function WorkOrderItemChangeStatus({ workOrderItem }: Props) {
  const [modalOpened, modalAction] = useDisclosure();

  const intl = useIntl();

  const [colorScheme] = useColorScheme();

  const {
    company: { companyConfig },
  } = useWorkorderDetailContext();

  function statusItemModal() {
    const title =
      workOrderItem.status?.situation === WorkOrderItemStatus.Black ||
      workOrderItem.origin === WorkOrderItemOrigin.Typed
        ? 'Peça digitada'
        : workOrderItem.description;

    const Badge = ({ color }: { color: string }) => (
      <div
        style={{
          width: 24,
          height: 24,
          backgroundColor: color,
          borderRadius: 4,
        }}
      />
    );

    Modal.info({
      icon: null,
      title: (
        <StatusTag
          label={intl.formatMessage({
            id: `workOrders.${workOrderItem.status?.situation}`,
          })}
          color={workOrderItem.status?.situation.split('.').at(-1)}
        />
      ),
      okButtonProps: { block: true },
      okText: 'Fechar',
      content: (
        <>
          <Typography.Title level={4}>{title}</Typography.Title>
          <Divider />
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Group gap={10}>
                <Badge color="red" />
                <Statistic
                  title={intl.formatMessage({
                    id: `workOrders.domain.enums.work_order_item_status.red`,
                  })}
                  value={workOrderItem.status?.remaining}
                />
              </Group>
            </Col>

            <Col span={24}>
              <Group gap={10}>
                <Badge color="yellow" />
                <Statistic
                  title={intl.formatMessage({
                    id: `workOrders.domain.enums.work_order_item_status.yellow`,
                  })}
                  value={workOrderItem.status?.quoted}
                />
              </Group>
            </Col>

            <Col span={24}>
              <Group gap={10}>
                <Badge color="orange" />
                <Statistic
                  title={intl.formatMessage({
                    id: `workOrders.domain.enums.work_order_item_status.orange`,
                  })}
                  value={workOrderItem.status?.purchased}
                />
              </Group>
            </Col>

            {!companyConfig?.isAutomaticStock && (
              <Col span={24}>
                <Group gap={10}>
                  <Badge color="blue" />
                  <Statistic
                    title={intl.formatMessage({
                      id: `workOrders.domain.enums.work_order_item_status.blue`,
                    })}
                    value={workOrderItem.status?.reserved}
                  />
                </Group>
              </Col>
            )}

            <Col span={24}>
              <Group gap={10}>
                <Badge color="green" />
                <Statistic
                  title={intl.formatMessage({
                    id: `workOrders.domain.enums.work_order_item_status.green`,
                  })}
                  value={workOrderItem.status?.done}
                />
              </Group>
            </Col>
          </Row>
        </>
      ),
    });
  }

  const renderStatus = (item: WorkOrderItemData, style?: CSSProperties) => (
    <ItemStatusTag
      label={intl.formatMessage({
        id: `workOrders.${item.status?.situation}`,
      })}
      status={item.status?.situation}
      onClick={statusItemModal}
      style={style}
    />
  );

  if (workOrderItem.type === WorkOrderItemType.Kit) {
    const itemChildrens = workOrderItem.children?.filter(
      (child) => child.type === WorkOrderItemType.Part,
    );

    const maxStatus = 3;
    const restStatusLength = itemChildrens?.slice(maxStatus)?.length;

    return (
      <Group>
        {itemChildrens
          ?.filter((child) => !!child.status)
          ?.slice(0, itemChildrens.length > maxStatus ? maxStatus : undefined)
          .map((child, i) => (
            <div style={{ height: 20, marginLeft: i > 0 ? -7 : 0 }} key={i}>
              {renderStatus(child, {
                border: `1px solid ${
                  colorScheme === 'dark' ? '#999' : 'white'
                }`,
              })}
            </div>
          ))}
        {!!restStatusLength && (
          <ItemStatusTag
            label={`Há mais ${restStatusLength}`}
            color={colorScheme === 'dark' ? '#555' : 'lightgray'}
            style={{
              padding: 0,
              fontSize: 9,
              marginLeft: -7,
              border: `1px solid ${colorScheme === 'dark' ? '#999' : 'white'}`,
              fontWeight: 700,
            }}
          >
            {`+${restStatusLength}`}
          </ItemStatusTag>
        )}
      </Group>
    );
  }

  if (
    workOrderItem.type === WorkOrderItemType.Part &&
    (workOrderItem.origin === WorkOrderItemOrigin.Typed ||
      workOrderItem.origin === WorkOrderItemOrigin.Supplied)
  ) {
    return (
      <Group position="between">
        <ItemStatusTag
          label={
            workOrderItem.tag?.name ||
            (workOrderItem.origin === WorkOrderItemOrigin.Typed
              ? 'Peça digitada'
              : '-')
          }
          color={
            workOrderItem.tag?.color ||
            (workOrderItem.origin === WorkOrderItemOrigin.Typed
              ? 'black'
              : 'transparent')
          }
          onClick={() => statusItemModal()}
        />

        <EditIcon iconProps={{ onClick: modalAction.open }} />

        <ChangeStatusItemModal
          itemId={workOrderItem.id}
          open={modalOpened}
          onCancel={modalAction.close}
        />
      </Group>
    );
  }

  if (workOrderItem.status) {
    return (
      <ItemStatusTag
        label={intl.formatMessage({
          id: `workOrders.${workOrderItem.status?.situation}`,
        })}
        status={workOrderItem.status?.situation}
        onClick={statusItemModal}
      />
    );
  }

  return <>{`-`}</>;
}
